// config.js

export const isLocalDevelopment = process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

export const isBetaEnvironment = window.location.origin === 'https://beta-app.prismax.ai';

export const APP_URL = "https://app.prismax.ai/";

export const PRISMAX_BACKEND_URL = isLocalDevelopment
    ? 'http://127.0.0.1:8080'
    : (isBetaEnvironment
        ? 'https://beta-user.prismaxserver.com'
        : 'https://user.prismaxserver.com');

export const PRISMAX_BACKEND_Control_URL = isLocalDevelopment
    ? 'http://127.0.0.1:8081'
    : (isBetaEnvironment
        ? 'https://beta-teleop.prismaxserver.com'
        : 'https://teleop.prismaxserver.com');

export const PRISMAX_DATA_PIPELINE_URL = isLocalDevelopment
    ? 'http://127.0.0.1:8082'
    : (isBetaEnvironment
        ? 'https://app-prismax-data-pipeline-beta-1053158761087.us-west1.run.app'
        : 'https://data.prismaxserver.com');

// Email verification uses authenticated API requests, which cannot complete a
// Cloudflare browser challenge during cross-origin preflight. Use the same
// Cloud Run service directly, as beta already does, for admin downloads.
export const PRISMAX_ADMIN_DOWNLOADS_URL = isLocalDevelopment
    ? 'http://127.0.0.1:8082'
    : (isBetaEnvironment
        ? 'https://app-prismax-data-pipeline-beta-1053158761087.us-west1.run.app'
        : 'https://app-prismax-data-pipeline-1053158761087.us-west1.run.app');

// Hex (AI assistant) — Cloud Run direct URLs until custom domains are wired.
// TODO(custom-domains): After domain mapping + DNS, switch to https://beta-hex.prismaxserver.com
// and https://hex.prismaxserver.com (see Cloud Run → Domain mappings).
const HEX_CLOUD_BETA_RUN_URL =
    'https://app-prismax-hex-beta-1053158761087.us-west1.run.app';
const HEX_CLOUD_PROD_RUN_URL =
    'https://app-prismax-hex-1053158761087.us-west1.run.app';

const HEX_LOCAL_DEFAULT_URL = 'http://127.0.0.1:8090';

/**
 * Hex base URL resolution:
 * - When the page runs on localhost / 127.0.0.1 → always http://127.0.0.1:8090 (runtime;
 *   does not depend on stale CRA env from an old npm start).
 * - REACT_APP_PRISMAX_HEX_URL can override on hosted builds only.
 * - beta-app.prismax.ai / app.prismax.ai use Cloud Run URLs from origin.
 */
function resolveHexBaseUrl() {
    if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        if (host === 'localhost' || host === '127.0.0.1') {
            return HEX_LOCAL_DEFAULT_URL;
        }
    }

    const explicit = (process.env.REACT_APP_PRISMAX_HEX_URL || '').trim();
    if (explicit) {
        return explicit.replace(/\/$/, '');
    }

    if (isLocalDevelopment) {
        return HEX_LOCAL_DEFAULT_URL;
    }

    return isBetaEnvironment ? HEX_CLOUD_BETA_RUN_URL : HEX_CLOUD_PROD_RUN_URL;
}

export const PRISMAX_HEX_URL = resolveHexBaseUrl();

export const RPC_URL = "https://mainnet.helius-rpc.com/?api-key=c58d0d16-a456-466f-83cd-ad43b23414d0"

// EVM RPC URLs
export const ETH_RPC_URL = "https://damp-indulgent-arm.quiknode.pro/1debab5d9009a5ef17685914062fa6ca5fb68bb5";
export const BASE_RPC_URL = "https://rpc.ankr.com/multichain/cd6af10201a606f9c2a94c8b1a21482cb90809b9fa276a3a819fc731bd938f3a";
export const MONAD_RPC_URL = "https://rpc-mainnet.monadinfra.com/rpc/outV0dtkmOoNHQ2LOC29B4L02CsfmjHE";


/**
 * Returns the number of times the current page / Node process
 * has called the configured Solana RPC endpoint.
 */
let _rpcCallCount = 0;

// Runtime global (avoid ESLint 'globalThis' no-undef in some configs)
const runtimeGlobal = typeof window !== 'undefined' ? window : {};

export function getRpcCallCount() {
    return _rpcCallCount;
}


if (typeof runtimeGlobal.fetch === 'function' && !runtimeGlobal.__rpcWrapped) {
    const originalFetch = runtimeGlobal.fetch.bind(runtimeGlobal);

    runtimeGlobal.fetch = async (input, init) => {
        const url = typeof input === 'string' ? input : input?.url;

        if (url && url.startsWith(RPC_URL)) {
            _rpcCallCount += 1;

            if (process.env.NODE_ENV !== 'production') {
                const stackLines = (new Error().stack || '')
                    .split('\n')
                    .slice(2, 8)
                    .map(l => l.trim());

                console.log(
                    `\n[RPC] call #${_rpcCallCount}: ${url}` +
                    (stackLines.length ? `\n↳ Trace:\n  ${stackLines.join('\n  ')}` : '')
                );
            }
        }

        return originalFetch(input, init);
    };

    runtimeGlobal.__rpcWrapped = true;
}


let configCache = {
    usdtTokenAddress: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
    usdcTokenAddress: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
    prismaxRecevingAddress: '',
    prismaxEvmReceivingAddress: '',
    robotStatus: null,
    isFetched: false,
};

// This function does the actual fetching:
async function initConfig() {
    try {
        {
            const res = await fetch(`${PRISMAX_BACKEND_URL}/get_blockchain_config_address`);
            if (!res.ok) throw new Error('Failed to fetch blockchain config addresses');
            const data = await res.json();
            if (data) {
                if (data.verified_token_solana_usdt) {
                    configCache.usdtTokenAddress = data.verified_token_solana_usdt;
                }
                if (data.verified_token_solana_usdc) {
                    configCache.usdcTokenAddress = data.verified_token_solana_usdc;
                }
                if (data.prismax_receiving_address) {
                    configCache.prismaxRecevingAddress = data.prismax_receiving_address;
                }
                if (data.prismax_evm_receiving_address) {
                    configCache.prismaxEvmReceivingAddress = data.prismax_evm_receiving_address;
                }
            }
            try {
                const robot_res = await fetch(`${PRISMAX_BACKEND_Control_URL}/robots/status`);
                if (robot_res.ok) {
                    const robot_data = await robot_res.json();
                    if (robot_data) {
                        configCache.robotStatus = robot_data;
                    }
                } else if (isLocalDevelopment) {
                    console.warn(
                        `[config] Tele-op not reachable (${PRISMAX_BACKEND_Control_URL}/robots/status → ${robot_res.status}). ` +
                            'Start app_prismax_tele_op_services on 8081 (TEST_MODE=true) or ignore for data-only local dev.'
                    );
                    configCache.robotStatus = null;
                } else {
                    throw new Error('Failed to fetch robot status');
                }
            } catch (robotErr) {
                if (isLocalDevelopment) {
                    console.warn('[config] Tele-op robot status fetch failed; continuing without robots.', robotErr);
                    configCache.robotStatus = null;
                } else {
                    throw robotErr;
                }
            }

        }

        configCache.isFetched = true;

    } catch (error) {
        console.error('Error fetching config:', error);
        throw error;
    }
}

/**
 * getConfig: Lazy-loads the config if it hasn't been fetched yet.
 */
export async function getConfig() {
    if (!configCache.isFetched) {
        await initConfig();
    }

    return {
        usdtTokenAddress: configCache.usdtTokenAddress,
        usdcTokenAddress: configCache.usdcTokenAddress,
        prismaxRecevingAddress: configCache.prismaxRecevingAddress,
        prismaxEvmReceivingAddress: configCache.prismaxEvmReceivingAddress,
        robotStatus: configCache.robotStatus,
    };
}
